/**
 * Token utility functions for authentication
 */

export interface TokenPayload {
  exp: number;
  iat: number;
  sub?: string;
  iss?: string;
  jti?: string;
  roles?: string[];
  name?: string;
  phone_number?: string;
  user_pool?: string;
  session_id?: string;
  [key: string]: any;
}

/**
 * Parse JWT token and extract payload
 */
export const parseJwt = (token: string): TokenPayload | null => {
  try {
    // Check if it's a JWT token (has 3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

/**
 * Check if a token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = parseJwt(token);
    if (!payload) {
      // If it's not a JWT token (like refresh token), assume it's valid
      return false;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp < currentTime;
    
    return isExpired;
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true;
  }
};

/**
 * Check if a token is expiring soon (within specified minutes)
 */
export const isTokenExpiringSoon = (token: string, minutesThreshold: number = 5): boolean => {
  try {
    const payload = parseJwt(token);
    if (!payload) {
      // If it's not a JWT token (like refresh token), assume it's not expiring soon
      return false;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    const thresholdTime = currentTime + (minutesThreshold * 60);
    const isExpiringSoon = payload.exp < thresholdTime;
    
    return isExpiringSoon;
  } catch (error) {
    console.error('Error checking token expiry soon:', error);
    return true;
  }
};

/**
 * Get token expiry time in milliseconds
 */
export const getTokenExpiryTime = (token: string): number | null => {
  try {
    const payload = parseJwt(token);
    if (!payload) return null;
    
    return payload.exp * 1000;
  } catch (error) {
    console.error('Error getting token expiry time:', error);
    return null;
  }
};

/**
 * Get time until token expires in minutes
 */
export const getTimeUntilExpiry = (token: string): number | null => {
  try {
    const expiryTime = getTokenExpiryTime(token);
    if (!expiryTime) return null;
    
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;
    return Math.max(0, Math.floor(timeUntilExpiry / (1000 * 60)));
  } catch (error) {
    console.error('Error getting time until expiry:', error);
    return null;
  }
};

/**
 * Get user information from token
 */
export const getUserFromToken = (token: string): Partial<TokenPayload> | null => {
  try {
    const payload = parseJwt(token);
    if (!payload) return null;
    
    return {
      sub: payload.sub,
      name: payload.name,
      phone_number: payload.phone_number,
      roles: payload.roles,
      user_pool: payload.user_pool,
      session_id: payload.session_id,
    };
  } catch (error) {
    console.error('Error getting user from token:', error);
    return null;
  }
};

/**
 * Get token expiry information for debugging
 */
export const getTokenInfo = (token: string) => {
  try {
    const payload = parseJwt(token);
    if (!payload) {
      // For non-JWT tokens, return basic info
      return {
        isJWT: false,
        tokenType: 'Non-JWT',
        tokenLength: token.length,
        user: null,
      };
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = payload.exp - currentTime;
    
    return {
      isJWT: true,
      exp: payload.exp,
      iat: payload.iat,
      currentTime,
      timeUntilExpiry,
      isExpired: payload.exp < currentTime,
      expDate: new Date(payload.exp * 1000).toISOString(),
      iatDate: new Date(payload.iat * 1000).toISOString(),
      currentDate: new Date().toISOString(),
      user: getUserFromToken(token),
    };
  } catch (error) {
    console.error('Error getting token info:', error);
    return null;
  }
};
